import { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useTheme } from '../context/ThemeContext';
import '../styles/ThemeCustomization.css';

function ThemeCustomization() {
  const { theme: currentTheme, updateTheme, defaultTheme, loading } = useTheme();
  const [formData, setFormData] = useState({ ...defaultTheme, ...currentTheme });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('primary');

  useEffect(() => {
    setFormData({ ...defaultTheme, ...currentTheme });
  }, [currentTheme, defaultTheme]);

  // Show loading state while fetching theme
  if (loading) {
    return (
      <div className="tc-container">
        <div className="tc-loading">Loading theme settings...</div>
      </div>
    );
  }

  const handleColorChange = (field, value) => {
    const newTheme = { ...formData, [field]: value };
    setFormData(newTheme);
    updateTheme(newTheme); // Live preview
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await axios.put('/company/theme', formData);
      setMessage('Theme saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to save theme: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to the default theme? This cannot be undone.')) {
      return;
    }
    
    setSaving(true);
    setMessage('');
    try {
      await axios.post('/company/theme/reset');
      setFormData(defaultTheme);
      updateTheme(defaultTheme);
      setMessage('Theme reset to default successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to reset theme: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const ColorPicker = ({ label, field, value, description }) => (
    <div className="tc-color-picker">
      <div className="tc-color-header">
        <label>{label}</label>
        {description && <span className="tc-color-description">{description}</span>}
      </div>
      <div className="tc-color-input-group">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => handleColorChange(field, e.target.value)}
          className="tc-color-input"
        />
        <input
          type="text"
          value={value || '#000000'}
          onChange={(e) => handleColorChange(field, e.target.value)}
          className="tc-color-text"
          pattern="^#[0-9A-Fa-f]{6}$"
          maxLength="7"
        />
      </div>
    </div>
  );

  return (
    <div className="tc-container">
      <div className="tc-header">
        <div>
          <h1>Theme Customization</h1>
          <p>Customize your company's color scheme and branding</p>
        </div>
        <div className="tc-actions">
          <button onClick={handleReset} className="tc-btn-secondary" disabled={saving}>
            Reset to Default
          </button>
          <button onClick={handleSave} className="tc-btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Theme'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`tc-message ${message.includes('Failed') ? 'tc-message-error' : 'tc-message-success'}`}>
          {message}
        </div>
      )}

      <div className="tc-content">
        <div className="tc-tabs">
          <button
            className={`tc-tab ${activeTab === 'primary' ? 'active' : ''}`}
            onClick={() => setActiveTab('primary')}
          >
            Primary Colors
          </button>
          <button
            className={`tc-tab ${activeTab === 'backgrounds' ? 'active' : ''}`}
            onClick={() => setActiveTab('backgrounds')}
          >
            Backgrounds
          </button>
          <button
            className={`tc-tab ${activeTab === 'menu' ? 'active' : ''}`}
            onClick={() => setActiveTab('menu')}
          >
            Menu/Sidebar
          </button>
          <button
            className={`tc-tab ${activeTab === 'text' ? 'active' : ''}`}
            onClick={() => setActiveTab('text')}
          >
            Text & Borders
          </button>
          <button
            className={`tc-tab ${activeTab === 'status' ? 'active' : ''}`}
            onClick={() => setActiveTab('status')}
          >
            Status Colors
          </button>
        </div>

        <div className="tc-panels">
          {activeTab === 'primary' && (
            <div className="tc-panel">
              <h3>Primary Colors</h3>
              <p className="tc-panel-description">These colors are used for buttons, links, and interactive elements</p>
              <div className="tc-color-grid">
                <ColorPicker
                  label="Primary Color"
                  field="primaryColor"
                  value={formData.primaryColor}
                  description="Main button and link color"
                />
                <ColorPicker
                  label="Primary Hover"
                  field="primaryHoverColor"
                  value={formData.primaryHoverColor}
                  description="Hover state for primary elements"
                />
                <ColorPicker
                  label="Secondary Color"
                  field="secondaryColor"
                  value={formData.secondaryColor}
                  description="Secondary buttons and accents"
                />
              </div>
            </div>
          )}

          {activeTab === 'backgrounds' && (
            <div className="tc-panel">
              <h3>Background Colors</h3>
              <p className="tc-panel-description">Configure the background colors for different areas</p>
              <div className="tc-color-grid">
                <ColorPicker
                  label="Main Background"
                  field="backgroundColor"
                  value={formData.backgroundColor}
                  description="Page background color"
                />
                <ColorPicker
                  label="Card Background"
                  field="cardBackground"
                  value={formData.cardBackground}
                  description="Content cards and panels"
                />
              </div>
            </div>
          )}

          {activeTab === 'menu' && (
            <div className="tc-panel">
              <h3>Menu & Sidebar Colors</h3>
              <p className="tc-panel-description">Customize the sidebar navigation appearance</p>
              <div className="tc-color-grid">
                <ColorPicker
                  label="Sidebar Background"
                  field="sidebarBackground"
                  value={formData.sidebarBackground}
                  description="Sidebar background color"
                />
                <ColorPicker
                  label="Menu Text"
                  field="menuTextColor"
                  value={formData.menuTextColor}
                  description="Menu item text color"
                />
                <ColorPicker
                  label="Menu Hover Background"
                  field="menuHoverBg"
                  value={formData.menuHoverBg}
                  description="Background when hovering menu items"
                />
                <ColorPicker
                  label="Menu Active Background"
                  field="menuActiveBg"
                  value={formData.menuActiveBg}
                  description="Active menu item background"
                />
                <ColorPicker
                  label="Menu Active Text"
                  field="menuActiveText"
                  value={formData.menuActiveText}
                  description="Active menu item text color"
                />
              </div>
            </div>
          )}

          {activeTab === 'text' && (
            <div className="tc-panel">
              <h3>Text & Borders</h3>
              <p className="tc-panel-description">Text colors and border styles</p>
              <div className="tc-color-grid">
                <ColorPicker
                  label="Primary Text"
                  field="textPrimary"
                  value={formData.textPrimary}
                  description="Main text color"
                />
                <ColorPicker
                  label="Secondary Text"
                  field="textSecondary"
                  value={formData.textSecondary}
                  description="Labels and subtitles"
                />
                <ColorPicker
                  label="Icon Color"
                  field="iconColor"
                  value={formData.iconColor}
                  description="Color for SVG icons and symbols"
                />
                <ColorPicker
                  label="Border Color"
                  field="borderColor"
                  value={formData.borderColor}
                  description="Borders and dividers"
                />
              </div>
            </div>
          )}

          {activeTab === 'status' && (
            <div className="tc-panel">
              <h3>Status Colors</h3>
              <p className="tc-panel-description">Colors for success, warning, and error states</p>
              <div className="tc-color-grid">
                <ColorPicker
                  label="Success Color"
                  field="successColor"
                  value={formData.successColor}
                  description="Success messages and badges"
                />
                <ColorPicker
                  label="Warning Color"
                  field="warningColor"
                  value={formData.warningColor}
                  description="Warning alerts"
                />
                <ColorPicker
                  label="Error Color"
                  field="errorColor"
                  value={formData.errorColor}
                  description="Error messages"
                />
              </div>
            </div>
          )}
        </div>

        {/* Preview Section */}
        <div className="tc-preview">
          <h3>Live Preview</h3>
          <div className="tc-preview-content">
            <button className="tc-preview-btn-primary">Primary Button</button>
            <button className="tc-preview-btn-secondary">Secondary Button</button>
            <div className="tc-preview-card">
              <h4>Sample Card</h4>
              <p className="tc-preview-text-primary">This is primary text</p>
              <p className="tc-preview-text-secondary">This is secondary text</p>
              <div className="tc-preview-badges">
                <span className="tc-preview-badge-success">Success</span>
                <span className="tc-preview-badge-warning">Warning</span>
                <span className="tc-preview-badge-error">Error</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThemeCustomization;
